import { ActionIcon, Box, Button, Center, Stack, Text } from "@dehub/theme";
import { CheckCircleIcon, XIcon } from "@phosphor-icons/react";
import { getModuleUrl, i18n, notify, useTenant } from "@dehub/theme";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PubGeneralApi } from "@/apis";
import { message } from "@/utils/message";
import { renderName } from "@/utils/render-name";
import { renderBusinessName } from "@/utils/render-business-name";

const RESEND_COOLDOWN_SECONDS = 60;

type Props = {
  action: any;
  setAction: (action: any[]) => void;
};

export function VerifyPanel({ action, setAction }: Props) {
  const { t } = useTranslation("register");
  const tenant = useTenant();
  const baseUrl = getModuleUrl("", tenant.modulesDomain);
  const userName = renderName(action?.[1]);
  const businessName = renderBusinessName({
    businessName: action?.[1]?.businessName,
    businessNameEng: action?.[1]?.businessNameEng,
  });

  const handleClose = () => {
    window.location.href = `${baseUrl}/login`;
    setAction([]);
  };

  const handleVerify = () => {
    window.location.href = `${baseUrl}/registration/${action?.[1]?.verifyId}`;
  };

  const [resending, setResending] = useState(false);
  // The panel opens right after register sent a code, so the first resend waits the same cooldown.
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    const verifyId = action?.[1]?.verifyId;
    const email = action?.[1]?.email;
    if (!verifyId || !email) return;

    setResending(true);
    try {
      await PubGeneralApi.resendRegisterCode({ verifyId, email });
      message.success(t("verify.resendSuccess", { email }));
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error: any) {
      if (error?.statusCode === 429) {
        const retryAfter = Number(error?.payload?.retryAfter);
        const seconds = Number.isFinite(retryAfter) && retryAfter > 0 ? Math.ceil(retryAfter) : RESEND_COOLDOWN_SECONDS;
        notify.error({
          title: i18n.t("error.title", { ns: "common" }),
          message: t("verify.resendRateLimited", { seconds }),
        });
        setCooldown(seconds);
      } else {
        message.error(error?.message);
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <Box style={{ overflow: "hidden", borderRadius: 8 }}>
      <Box pos="relative" style={{ background: "var(--mantine-color-brand-6)" }} p="xl">
        <ActionIcon
          pos="absolute"
          top={16}
          right={16}
          onClick={handleClose}
          variant="subtle"
          c="white"
          style={{ opacity: 0.8 }}>
          <XIcon size={20} />
        </ActionIcon>

        <Text ta="center" c="white" fz={20} fw={600}>
          {t("verify.successTitleTpl", { tenant: tenant.name || "DeHUB" })}
        </Text>

        <Center my="md">
          <CheckCircleIcon size={80} color="white" />
        </Center>

        <Text ta="center" c="white" fz={14} px="md">
          {t("verify.successGreeting")}{" "}
          <Text component="span" fw={700}>
            {userName}
          </Text>{" "}
          {t("verify.successMiddle")}{" "}
          <Text component="span" fw={700}>
            {businessName}
          </Text>
          {t("verify.successBody", { tenant: tenant.name || "DeHUB" })}
        </Text>
      </Box>

      <Box p="xl" bg="white">
        <Stack gap={4} mb="md">
          <Text c="gray.6" fz={14}>
            {t("verify.orgName")}
          </Text>
          <Text c="var(--mantine-color-brand-6)" fw={600} fz={16}>
            {businessName}
          </Text>
        </Stack>

        <Stack gap={4} mb="xl">
          <Text c="gray.6" fz={14}>
            {t("verify.userName")}
          </Text>
          <Text c="var(--mantine-color-brand-6)" fw={600} fz={16}>
            {userName}
          </Text>
        </Stack>

        <Button fullWidth h={44} radius="md" color="brand" onClick={handleVerify}>
          {t("verify.verifyBtn")}
        </Button>

        <Stack gap="xs" mt="lg" align="center">
          <Text ta="center" c="gray.7" fz={14}>
            {t("verify.resendText")}
          </Text>
          <Button
            variant="subtle"
            color="brand"
            fz={14}
            loading={resending}
            disabled={cooldown > 0}
            onClick={handleResend}>
            {cooldown > 0 ? t("verify.resendCooldown", { seconds: cooldown }) : t("verify.resendBtn")}
          </Button>
        </Stack>

        <Text ta="center" c="gray.5" fz={12} mt="md">
          {tenant.name || t("verify.company")}
        </Text>
      </Box>
    </Box>
  );
}
